/**
 * THE DATA-QUALITY ROLL-UP — the one read behind the Insights "gaps" panel.
 *
 * Every number a board leans on to manage itself is only as honest as the facts
 * the cards carry. WIP counts the cards sitting in To-do; aging counts the days a
 * card has gone without moving; the forecast counts sizes and due dates to say
 * when the work lands. A card missing the fact a number relies on does not just
 * go uncounted — it DISTORTS the number, quietly, in a direction nobody can see:
 *
 *   · AN IDLE CARD inflates aging by being the stalest thing on the board while
 *     looking, from a glance at the column, like work in flight.
 *   · A STALE TO-DO with no due date is WIP that will never leave To-do on its
 *     own — it makes the "started vs committed" ratio read worse than the team is
 *     actually doing, because the eleven in To-do are not really eleven.
 *   · AN UNSIZED card contributes nothing to a velocity or a burndown, so a
 *     fortnight of real work can add up to a suspiciously light sprint.
 *   · A CARD WITH NO DUE DATE cannot be on or behind schedule, so it falls out of
 *     the forecast entirely rather than showing as the risk it is.
 *
 * So this module rolls those gaps up for one surface to show, and — just as
 * important — says which of the tracked dimensions are CLEAN, so the panel can
 * show a green "all set" line rather than only ever nagging. A board that has
 * tidied its sizes should be told so.
 *
 * IT REUSES THE RULES RATHER THAN REINVENTING THEM. "Idle" is exactly the
 * idle-nudge signal (src/idle-nudge.ts) — same movement maths, same working-day
 * count, same size-scaled window, so the gaps panel can never disagree with the
 * card's own idle badge. "Suggestable" is exactly what the one-tap suggesters
 * (src/suggest.ts) would offer — so a count of "12 we could fill for you" is the
 * literal number of cards those suggesters would return a value for, not a looser
 * estimate of it. Rebuilding either rule here would be a second place for them to
 * drift.
 *
 * PURE ON PURPOSE, exactly like src/idle-nudge.ts, src/suggest.ts,
 * src/dependencies.ts and src/progress-health.ts — no store, no React, no clock
 * of its own. The caller passes the cards straight from the store, the phases and
 * sprints (so the no-due "suggestable" count can ask the due-date suggester with
 * each card's real phase and sprint window), the idle windows and `nowMs`. That is
 * what lets every definition below be proved against fixed fixtures in
 * scripts/test-data-quality.ts rather than argued about.
 *
 * THE COPY LIVES IN THE UI, NOT HERE. This module returns data and enums only —
 * counts, the card ids behind each count, and which dimensions are clean. The
 * wording of each row ("11 cards inflating WIP") is the surface's to write, so the
 * numbers can be re-skinned without touching the rules.
 */
import type { Issue } from './types';
import type { Phase } from './phases';
import type { Sprint } from './sprints';
import { idleCards, lastMovementMs, type IdleWindows } from './idle-nudge';
import { workingDaysBetween, DEFAULT_CALENDAR, type WorkCalendar } from './work-calendar';
import { suggestSize, suggestDue } from './suggest';
import { ownerOf } from './forecast';

/**
 * HOW MANY WORKING DAYS A TO-DO CARD WITH NO DUE DATE MAY SIT before it counts as
 * a stale to-do inflating WIP. Fifteen working days is three working weeks — long
 * enough that a card still legitimately queued is not flagged, short enough that
 * a card nobody is ever going to start is. A fixed board-wide number rather than
 * the size-scaled idle window, because this gap is about COMMITMENT going stale in
 * the queue, not about a doer letting started work go quiet — a different question
 * with a different, blunter threshold.
 */
export const STALE_TODO_DAYS = 15;

/** The four gap kinds, in the fixed order the panel lists them. */
export type DQKind = 'idle' | 'stale-todo' | 'no-size' | 'no-due';

/**
 * A DIMENSION THAT CAN READ "CLEAN" — the four gap kinds, plus `epic-area`, which
 * is a clean-only dimension (every active card has at least one of epic/area) and
 * is NEVER a gap row of its own: grouping gaps are reported as a single tidy/not
 * signal, not a list of cards to chase.
 */
export type DQCleanDim = DQKind | 'epic-area';

/**
 * ONE GAP ROW — a kind, how many cards are in it, and WHICH cards, so a surface
 * can both show the number and let the reader open the cards behind it.
 */
export interface DQRow {
  kind: DQKind;
  count: number;
  /** The ids of the cards in this gap, in the order the kind produces them. */
  ids: string[];
  /**
   * HOW MANY OF THEM THE ONE-TAP SUGGESTERS COULD FILL — the "and we could fill N
   * for you" figure. Always 0 for `idle` and `stale-todo`: neither is a missing
   * field a suggester offers a value for, it is a card that needs a human to move
   * it, so there is nothing to one-tap.
   */
  suggestable: number;
  /** `stale-todo` only: every card in To-do right now — the WIP the gap inflates. */
  wipNow?: number;
  /** `stale-todo` only: WIP once the stale cards are cleared — `wipNow` − `count`. */
  wipAfter?: number;
}

export interface DataQuality {
  /** The gap rows with a count > 0, in the fixed order: idle, stale-todo, no-size, no-due. */
  rows: DQRow[];
  /** The total number of cards needing review — the sum of the rows' counts. */
  toReview: number;
  /** The tracked dimensions that are fully clean, for the green "all set" lines. */
  cleanDims: DQCleanDim[];
  /** Nothing to review — `rows.length === 0`. */
  allClear: boolean;
  /** How many active cards there are — the denominator the panel reads the gaps against. */
  activeCount: number;
}

/** Done or dropped: finished work, never a gap. */
function isResolved(s: Issue['status']): boolean {
  return s === 'done' || s === 'wontdo';
}

/**
 * ACTIVE WORK — not resolved, and not in the backlog. A backlog card is parked on
 * purpose, not forgotten, so a blank on it is not a gap the way a blank on
 * committed work is; the idle-nudge module makes the same call for the same
 * reason.
 */
function isActive(s: Issue['status']): boolean {
  return !isResolved(s) && s !== 'backlog';
}

/** Work that has actually started — the lanes a doer is moving, where going quiet
 *  is an IDLE card rather than a stale queue. (A quiet To-do is the stale-todo
 *  case, handled on its own, so idle is measured over STARTED cards only and the
 *  two never double-count the same card.) */
function isStarted(s: Issue['status']): boolean {
  return s === 'inprogress' || s === 'staging' || s === 'inreview';
}

/** Whether a field actually names a value — the src/suggest.ts rule: null, absent
 *  and '' all read as "not set". Used for the due date, the size, the epic and the area. */
function hasDay(d: string | null | undefined): d is string {
  return typeof d === 'string' && d.length > 0;
}

/**
 * THE STALE TO-DO CARDS on a board — a To-do card with no due date that has not
 * moved in STALE_TODO_DAYS working days. One source of truth: the Insights panel
 * (dataQuality) and the board-face strip both read this, so they agree on exactly
 * which cards count. Pure: hand it the board's issues and now.
 */
export function staleTodoCards(issues: Issue[], nowMs: number, cal: WorkCalendar = DEFAULT_CALENDAR): Issue[] {
  return issues.filter(
    (i) =>
      i.status === 'todo' &&
      !hasDay(i.dueDate) &&
      workingDaysBetween(lastMovementMs(i), nowMs, cal) >= STALE_TODO_DAYS,
  );
}

/**
 * THE DATA-QUALITY ROLL-UP for a board.
 *
 * `issues` arrive straight from the store; an Issue satisfies both `SuggestCard`
 * (so it passes into the suggesters, and is itself a usable comparable) and
 * `IdleCard` (so it passes into the idle signal), so the pool a rule learns from
 * is simply `issues`. `phases` and `sprints` are needed only so the no-due
 * suggestable count can ask the due-date suggester with each card's real phase and
 * sprint window; `windows` and `nowMs` are the idle signal's own arguments.
 */
export function dataQuality(
  issues: Issue[],
  phases: Phase[],
  sprints: Sprint[],
  windows: IdleWindows,
  nowMs: number,
  cal: WorkCalendar = DEFAULT_CALENDAR,
): DataQuality {
  const active = issues.filter((i) => isActive(i.status));

  // 1 — IDLE, measured over STARTED cards only (so a quiet To-do is the stale-todo
  // case below, not this one — no card is counted in both). The idle-nudge module
  // owns the movement maths and the size-scaled window; we only hand it the pool.
  const started = issues.filter((i) => isStarted(i.status));
  const idleIds = idleCards(started, windows, nowMs, cal).map((r) => r.card.id);

  // 2 — STALE TO-DO: a To-do card with no due date that has not moved in
  // STALE_TODO_DAYS working days. `wipNow` is every To-do card; `wipAfter` is what
  // WIP reads once these stale ones are cleared. The predicate is `staleTodoCards`
  // so the board-face strip and this panel can never disagree about which cards.
  const todo = issues.filter((i) => i.status === 'todo');
  const staleTodoIds = staleTodoCards(issues, nowMs, cal).map((i) => i.id);
  const wipNow = todo.length;
  const wipAfter = wipNow - staleTodoIds.length;

  // 3 — NO SIZE: any UNRESOLVED card with no size, INCLUDING the backlog. Unlike
  // the other gaps, a size is owed the moment a card exists — you estimate the work
  // to be able to plan it, before it is ever committed or started (Vinnie, 28 Oct).
  // So the sizing gap spans the whole board bar finished cards, not just the active
  // lanes. (No-due below deliberately stays active-only: you date work once it is
  // committed, not while it is parked in the backlog.) Suggestable = how many the
  // size suggester would offer a value for, learning from the whole board.
  const unresolved = issues.filter((i) => !isResolved(i.status));
  const noSize = unresolved.filter((i) => !hasDay(i.size));
  const noSizeIds = noSize.map((i) => i.id);
  const noSizeSuggestable = noSize.filter((i) => suggestSize(i, issues) !== null).length;

  // 4 — NO DUE: active cards with no due date. Suggestable = how many the due-date
  // suggester would offer a value for, given each card's own phase and sprint
  // window (matched by id) and the whole board as the duration pool.
  const phaseOf = (i: Issue): Pick<Phase, 'name' | 'targetEnd'> | null => {
    const p = i.phase ? phases.find((ph) => ph.id === i.phase) : undefined;
    return p ? { name: p.name, targetEnd: p.targetEnd } : null;
  };
  const sprintOf = (i: Issue): Pick<Sprint, 'name' | 'endsOn'> | null => {
    const s = i.sprint ? sprints.find((sp) => sp.id === i.sprint) : undefined;
    return s ? { name: s.name, endsOn: s.endsOn } : null;
  };
  const noDue = active.filter((i) => !hasDay(i.dueDate));
  const noDueIds = noDue.map((i) => i.id);
  // The personal source (Layer B, piece D): the card's doer and the board's finished
  // cards to read their pace from. Owner-less cards get no personal read. Same shape
  // the panel's Fill builds, so the count and the fill stay in step.
  // History always supplied so piece C (comparable cycle-time) can date an owner-less
  // card; piece D reads `owner` and skips when it is empty. Same shape the panel's Fill
  // builds, so the count and the fill stay in step.
  const personalFor = (i: Issue) => ({ owner: ownerOf(i), history: issues, cal });
  // Count only the dues the suggester will actually stand behind — the same call,
  // with the same `nowMs` and personal source, that the panel's "Fill" runs — so the
  // count and the fill never disagree (a past-dated source yields nothing, and must
  // not be counted).
  const noDueSuggestable = noDue.filter(
    (i) => suggestDue(i, phaseOf(i) ?? null, issues, sprintOf(i) ?? null, nowMs, personalFor(i)) !== null,
  ).length;

  // EPIC/AREA: clean-only. A card missing BOTH epic and area has no grouping at
  // all; a board where every active card carries at least one is clean on this
  // dimension. It is never a gap row — grouping is a tidy/not signal, not a chase list.
  const epicAreaMissing = active.filter((i) => !hasDay(i.epic) && !hasDay(i.area)).length;

  // The rows, built in the fixed order and then narrowed to the non-empty ones, so
  // `rows` is always idle → stale-todo → no-size → no-due with zero-count kinds dropped.
  const allRows: DQRow[] = [
    { kind: 'idle', count: idleIds.length, ids: idleIds, suggestable: 0 },
    {
      kind: 'stale-todo',
      count: staleTodoIds.length,
      ids: staleTodoIds,
      suggestable: 0,
      wipNow,
      wipAfter,
    },
    { kind: 'no-size', count: noSizeIds.length, ids: noSizeIds, suggestable: noSizeSuggestable },
    { kind: 'no-due', count: noDueIds.length, ids: noDueIds, suggestable: noDueSuggestable },
  ];
  const rows = allRows.filter((r) => r.count > 0);

  // A dimension is clean when its gap count is 0, in the same fixed order, with
  // epic-area last. epic-area's gap is the both-missing count, never a row above.
  const cleanDims: DQCleanDim[] = [];
  if (idleIds.length === 0) cleanDims.push('idle');
  if (staleTodoIds.length === 0) cleanDims.push('stale-todo');
  if (noSizeIds.length === 0) cleanDims.push('no-size');
  if (noDueIds.length === 0) cleanDims.push('no-due');
  if (epicAreaMissing === 0) cleanDims.push('epic-area');

  return {
    rows,
    toReview: rows.reduce((sum, r) => sum + r.count, 0),
    cleanDims,
    allClear: rows.length === 0,
    activeCount: active.length,
  };
}
